import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Building2, ChevronLeft } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { authenticatedFetch } from '../../utils/api';
import { API_HOST } from '../../utils/constants';
import { useUser } from '../../context/UserContext';
import PixelStage from './PixelStage';
import AvatarCustomizer from './AvatarCustomizer';
import TownResourceGallery from './TownResourceGallery';
import { loadAvatar, makeDefaultAvatar } from './avatar';
import { buildProjectFloorScene, truncateName } from './maps';
import { C } from './engine';
import { createTownProjectVisit, loadTownContinuation, saveTownContinuation } from './townContinuation';

/** 像素风加载骨架：深色舞台 + 抖动色块（上排集门 / 下排功能房） */
function FloorSkeleton() {
  const blocks = [
    { left: '14%', top: '18%', width: 64, height: 44 },
    { left: '34%', top: '18%', width: 64, height: 44 },
    { left: '54%', top: '18%', width: 64, height: 44 },
    { left: '74%', top: '18%', width: 64, height: 44 },
    { left: '14%', top: '66%', width: 88, height: 56 },
    { left: '38%', top: '66%', width: 88, height: 56 },
    { left: '62%', top: '66%', width: 88, height: 56 },
  ];
  return (
    <div className="w-full h-full relative" style={{ backgroundColor: C.wallDark }}>
      {blocks.map((b, i) => (
        <div key={i} className="skeleton absolute" style={{ ...b, opacity: 0.5, borderRadius: 2 }} />
      ))}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xs font-bold"
        style={{ color: C.cream, fontFamily: 'ui-monospace, Menlo, monospace' }}
      >
        正在生成楼层地图…
      </div>
    </div>
  );
}

/**
 * 像素办公室 · 项目楼层（三层结构的第 2 层）
 * 路由：/project/:projectId/office
 * 上方集门厅：每集一扇门，Enter 进入该集功能大厅；下方项目级功能房；出口 / Esc 返回大厅。
 */
export default function PixelProjectRoom() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { userId, user } = useUser();
  const displayName = user?.name || userId || '新同事';

  const [avatar, setAvatar] = useState(() => loadAvatar() || makeDefaultAvatar(displayName));
  const [hasSavedAvatar, setHasSavedAvatar] = useState(() => !!loadAvatar());
  const [showCustomizer, setShowCustomizer] = useState(() => !loadAvatar());
  const [showResourceGallery, setShowResourceGallery] = useState(false);
  const [projectName, setProjectName] = useState(location.state?.projectName || '');
  const [episodes, setEpisodes] = useState(null); // null = 加载中
  const [episodesFailed, setEpisodesFailed] = useState(false);
  const [episodesRetry, setEpisodesRetry] = useState(0);
  const retryPendingRef = useRef(false);

  // 项目名：优先 location.state，否则拉一次项目列表匹配（刷新/直链场景）
  useEffect(() => {
    if (projectName) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const list = await authenticatedFetch(`${API_HOST}/api/project/list`, {
          headers: { 'X-User-ID': userId },
        });
        if (cancelled) return;
        const found = Array.isArray(list) ? list.find((p) => String(p.id) === String(projectId)) : null;
        setProjectName(found?.name || found?.title || '项目空间');
      } catch (e) {
        if (!cancelled) {
          console.error('[pixel-office] 项目名加载失败', e);
          setProjectName('项目空间');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, userId, projectName]);

  useEffect(() => {
    if (!projectId || !projectName) return;
    saveTownContinuation(createTownProjectVisit(
      loadTownContinuation(), { id: projectId, name: projectName }, Date.now(),
    ));
  }, [projectId, projectName]);

  // 分集列表：驱动集门厅的门；失败时提示并仍渲染 0 集楼层
  useEffect(() => {
    let cancelled = false;
    setEpisodesFailed(false);
    setEpisodes(null);
    (async () => {
      try {
        const list = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
        if (cancelled) return;
        if (!Array.isArray(list)) throw new Error('Invalid episode list response');
        setEpisodes(list);
      } catch (e) {
        if (cancelled) return;
        console.error('[pixel-office] 分集列表加载失败', e);
        setEpisodesFailed(true);
        setEpisodes([]); // 失败仍展示空楼层（功能房可用）
      } finally {
        if (!cancelled) retryPendingRef.current = false;
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, episodesRetry]);

  const retryEpisodes = useCallback(() => {
    if (!episodesFailed || retryPendingRef.current) return;
    retryPendingRef.current = true;
    setEpisodesFailed(false);
    setEpisodes(null);
    setEpisodesRetry((attempt) => attempt + 1);
  }, [episodesFailed]);

  const scene = useMemo(
    () => {
      if (episodes === null) return null;
      const floor = buildProjectFloorScene(projectId, projectName || '项目空间', episodes);
      if (!episodesFailed) return floor;
      return {
        ...floor,
        labels: floor.labels.map((label) => (
          label.text === '该项目还没有集' ? { ...label, text: '分集列表加载失败' } : label
        )),
        npcs: floor.npcs.map((npc) => ({
          ...npc,
          lines: npc.lines.map((line) => (
            line === '这里还没有分集，项目功能房仍然可以使用。'
              ? '分集列表暂未加载成功，请点击重试；项目功能房仍然可以使用。'
              : line
          )),
        })),
      };
    },
    [projectId, projectName, episodes, episodesFailed],
  );

  const scriptedCount = useMemo(() => (
    Array.isArray(episodes)
      ? episodes.filter((episode) => String(episode?.scriptContent || '').trim()).length
      : 0
  ), [episodes]);

  const welcomeToasts = useMemo(() => {
    if (episodes === null) return [];
    if (episodesFailed) return ['分集列表暂未加载成功，可以点击重试；下方功能房仍可进入'];
    return episodes.length > 0
      ? [`当前 ${episodes.length} 集，${scriptedCount} 集已有剧本；实心点表示已有剧本`, '上方集门厅选集，下方是项目级功能房，走上门垫按 Enter 进入']
      : ['该项目还没有集，先去新建一集吧；下方功能房随时可逛'];
  }, [episodes, episodesFailed, scriptedCount]);

  const handleAction = useCallback((action) => {
    if (action?.type === 'route') {
      navigate(action.path, action.state ? { state: action.state } : undefined);
    } else if (action?.type === 'custom' && action.id === 'resource-gallery') {
      setShowResourceGallery(true);
    }
  }, [navigate]);

  const handleEscape = useCallback(() => navigate('/'), [navigate]);

  return (
    <div className="pixel-town-page pixel-town-page--standalone">
      <div className="pixel-town-toolbar">
        <div className="pixel-town-toolbar__identity">
          <span className="pixel-town-toolbar__mark"><Building2 size={15} /></span>
          <span className="pixel-town-toolbar__title">
            《{truncateName(projectName || '项目空间', 16)}》
          </span>
          {episodes !== null && (
            <span className="pixel-town-toolbar__meta pixel-town-toolbar__meta--secondary">
              {episodesFailed ? '分集加载失败' : `${episodes.length} 集 · ${scriptedCount} 集已有剧本`}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="pixel-town-button"
          title="返回大厅（Esc）"
        >
          <ChevronLeft size={14} /> <span className="pixel-town-button__label">返回大厅</span>
        </button>
      </div>

      {/* 舞台 */}
      <div className="pixel-town-stage-shell">
        {showResourceGallery ? (
          <TownResourceGallery
            projectId={projectId}
            projectName={projectName}
            avatar={avatar}
            onCustomizeAvatar={() => setShowCustomizer(true)}
            onClose={() => setShowResourceGallery(false)}
            onOpenResourceCenter={() => navigate(`/project/${projectId}/assets/resources`)}
          />
        ) : scene === null ? (
          <FloorSkeleton />
        ) : (
          <PixelStage
            scene={scene}
            avatar={avatar}
            paused={showCustomizer}
            welcomeToasts={welcomeToasts}
            onAction={handleAction}
            onEscape={handleEscape}
            onCustomizeAvatar={() => setShowCustomizer(true)}
          />
        )}

        {episodesFailed && scene !== null && !showResourceGallery && (
          <div className="pixel-town-error-note" role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, pointerEvents: 'auto', whiteSpace: 'normal' }}>
            <span>分集列表加载失败，先逛逛功能房吧</span>
            <button type="button" className="pixel-town-button" onClick={retryEpisodes}>重试</button>
          </div>
        )}

        {showCustomizer && (
          <AvatarCustomizer
            initialAvatar={hasSavedAvatar ? avatar : null}
            defaultName={displayName}
            allowCancel={hasSavedAvatar}
            onSave={(next) => { setAvatar(next); setHasSavedAvatar(true); setShowCustomizer(false); }}
            onCancel={() => setShowCustomizer(false)}
          />
        )}

      </div>
    </div>
  );
}
